import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/layout'
import { CalendarDate, getWeeksInMonth } from '@internationalized/date'
import { CalendarState } from '@react-stately/calendar'
import { useIntl } from 'react-intl'
import { SchedulerTranslationsTokens } from 'shared-utils'

import { CALENDER_QUERIES } from '../../Calendar/styles/responsive'
import { useSchedulerContext } from '..'
import { createColorFromPrimaryText } from '../utils/colors'
import { CalendarCell } from './CalendarCell'

export const CalendarGrid = () => {
	return (
		<Flex w="full" h="full" direction="column">
			<WeekDaysRow />
			<WeekCells />
		</Flex>
	)
}

const WeekDaysRow = () => {
	const intl = useIntl()
	const { styles } = useSchedulerContext()
	const WEEKS = intl
		.formatMessage({ id: SchedulerTranslationsTokens.weekdays_short })
		.split('_')

	const baseColor = createColorFromPrimaryText(styles.text_primary)

	return (
		<Grid w="full" templateColumns="repeat(7, 1fr)" gap="4px">
			{WEEKS.map((day, i) => (
				<GridItem
					key={i}
					display="flex"
					justifyContent="center"
					alignItems="center"
					h="45px"
					w="45px"
					sx={{
						[CALENDER_QUERIES.xs]: {
							h: '36px',
							w: '36px',
						},
					}}
				>
					<Text
						fontSize="12px"
						fontWeight={500}
						color={
							i === 0 || i === 6
								? baseColor.fade(0.7).hexa()
								: baseColor.fade(0.35).hexa()
						}
					>
						{day}
					</Text>
				</GridItem>
			))}
		</Grid>
	)
}

const WeekCells = () => {
	const { calendarState, isAvailabilityLoading, availableTimes } =
		useSchedulerContext()

	if (!calendarState) return null

	const noAvailability = !isAvailabilityLoading && availableTimes.length === 0

	const getMonthWeeks = (calendarState: CalendarState): CalendarDate[][] => {
		const weeksInMonth = getWeeksInMonth(
			calendarState.visibleRange.start,
			'en-US',
		)
		return Array.from({ length: weeksInMonth }, (_, weekIndex) =>
			calendarState
				.getDatesInWeek(weekIndex, calendarState.visibleRange.start)
				.filter((date): date is CalendarDate => date !== null),
		)
	}

	const weeks = getMonthWeeks(calendarState)

	return (
		<Box position="relative">
			{noAvailability && <ErrorBanner />}
			<Grid
				w="full"
				templateColumns="repeat(7, 1fr)"
				gap="4px"
				fontWeight={500}
				bg="transparent"
				filter={noAvailability ? 'blur(3px) grayscale(0.5)' : 'none'}
				opacity={noAvailability ? 0.5 : 1}
				pointerEvents={noAvailability ? 'none' : 'auto'}
				transition="filter 0.2s, opacity 0.2s"
			>
				{weeks.map((week) =>
					week.map((date, i) => {
						if (date) {
							return <CalendarCell key={i} date={date} index={i} />
						}
					}),
				)}
			</Grid>
		</Box>
	)
}

const ErrorBanner = () => {
	const { styles } = useSchedulerContext()

	return (
		<Flex
			position="absolute"
			w="full"
			maxW="210px"
			align="center"
			justify="center"
			left="50%"
			top="50%"
			transform="translateX(-50%) translateY(-50%)"
			bg={styles.bg_primary}
			borderRadius="8px"
			border="1px solid"
			borderColor={styles.border_primary}
			px={3}
			py={2}
			boxShadow="sm"
		>
			<Text
				fontSize="14px"
				color={styles.text_primary}
				fontWeight={500}
				textAlign="center"
			>
				No availability this month
			</Text>
		</Flex>
	)
}
